import React from 'react';
import { Link } from 'react-router-dom';
import { CheckOutlined, ArrowRightOutlined } from '@ant-design/icons';
import useReveal from '../AIEmployees/useReveal';
import './HomePage.css';

/**
 * HomeProduct — a generic product-intro beat for the homepage hub. Renders the
 * eyebrow / H2 / sub head, an optional grid of benefit bullets, an optional
 * pre-CTA slot (e.g. the function-chip strip), a CTA row, an optional support
 * line under the CTAs, and children (in-section visuals / crawlable prose).
 * The product's signature visual (SAShowcase / A2AStat) is composed
 * immediately AFTER this section by HomePage where it is a shared component,
 * keeping the established one-section-one-file rhythm instead of nesting.
 *
 * CTAs: `primary` is an action button (e.g. the hire flow); `link` is a real
 * crawlable <Link> to the product's dedicated page — the homepage is the
 * highest-authority URL, so these links deliberately pass equity to the
 * product pages.
 *
 * `link.onClick` is how that link reports itself (Aug 22). Until now these
 * section links fired NO cta_click, so every per-CTA ranking silently omitted
 * them — they read as "zero clicks" when they were simply never counted. The
 * chips beside them were tracked; the links were not. Callers pass a tracker.
 */
const HomeProduct = ({
  id,
  eyebrow,
  title,
  titleAccent,
  sub,
  bullets,
  preCta,
  primary,
  link,
  supportLine,
  children,
}) => {
  const [ref, visible] = useReveal();

  return (
    <section className="home-product aie-section" id={id} ref={ref}>
      <div className={`aie-reveal ${visible ? 'is-visible' : ''}`}>
        <div className="aie-section-head">
          <span className="aie-eyebrow">{eyebrow}</span>
          <h2 className="aie-section-title aie-display">
            {title}
            {titleAccent && (
              <>
                <br />
                <span className="home-product__title-accent">{titleAccent}</span>
              </>
            )}
          </h2>
          <p className="aie-section-sub">{sub}</p>
        </div>

        {bullets && bullets.length > 0 && (
          <ul className="home-product__bullets">
            {bullets.map((b) => (
              <li className="home-product__bullet" key={b}>
                <CheckOutlined aria-hidden="true" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}

        {preCta}

        <div className="home-product__ctas">
          {primary && (
            <button
              type="button"
              data-msc-suppress
              className="aie-btn aie-btn-primary"
              onClick={primary.onClick}
            >
              {primary.label}
              <ArrowRightOutlined />
            </button>
          )}
          {link && (
            <Link className="home-product__link" to={link.to} onClick={link.onClick}>
              {link.label} <ArrowRightOutlined />
            </Link>
          )}
        </div>

        {supportLine && <p className="home-product__support">{supportLine}</p>}

        {children}
      </div>
    </section>
  );
};

export default HomeProduct;
